// Phase 3 follow-up — Tenant-view "accidental drop" breadcrumb.
//
// context.tsx documents (and the operator has confirmed) that tenant-view
// selection is intentionally SESSION state only (in-memory useState) — it
// must never survive a reload, because an admin must never be silently left
// impersonating a tenant across reloads or a shared link. That security
// property means the app has no memory that it was ever in tenant view once
// the page reloads, so it can't tell the operator "you just lost scope".
//
// This module closes that gap without weakening the property above: it
// writes a lightweight, non-restorable BREADCRUMB (not the live selection)
// to sessionStorage whenever a platform admin enters tenant view. If the
// app later mounts with no tenant selected but the breadcrumb is still
// present, that's the signature of an unexpected drop (hard navigation,
// manual reload, crash) rather than a deliberate "Exit tenant view" click —
// exiting explicitly clears the breadcrumb (see context.tsx's
// setSelectedTenant). See TenantViewDroppedNotice.tsx for the surfaced UI.
//
// Security notes:
//   - sessionStorage, not localStorage — cleared when the tab/window closes,
//     consistent with the "no persistence across sessions" rule in context.tsx.
//   - The breadcrumb is display-only. Reading it NEVER re-applies tenant
//     scope by itself — TenantViewDroppedNotice requires an explicit
//     operator click ("Return to <tenant>") which re-fetches the tenant
//     canonically before calling setSelectedTenant. See that component.
//   - Tenant name/code are not PII (TenantSelectorBand's own security note);
//     safe to store and render.

const STORAGE_KEY = 'antifraud:tenant-view-marker'

export interface TenantViewMarker {
  public_id: string
  name: string
  code: string
}

function isTenantViewMarker(value: unknown): value is TenantViewMarker {
  if (typeof value !== 'object' || value === null) return false
  const rec = value as Record<string, unknown>
  return (
    typeof rec.public_id === 'string' &&
    typeof rec.name === 'string' &&
    typeof rec.code === 'string'
  )
}

/**
 * Write (or overwrite) the breadcrumb for the tenant currently being
 * viewed. Called whenever a platform admin selects/switches to a tenant.
 * Best-effort: sessionStorage may be unavailable (private browsing /
 * disabled storage) — the breadcrumb is a UX convenience only, never a
 * security control, so failures here must not block tenant selection.
 */
export function writeTenantViewMarker(marker: TenantViewMarker): void {
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ public_id: marker.public_id, name: marker.name, code: marker.code }),
    )
  } catch {
    // Storage unavailable — no-op. See module doc.
  }
}

/** Read the breadcrumb, if present and well-formed. */
export function readTenantViewMarker(): TenantViewMarker | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return isTenantViewMarker(parsed) ? parsed : null
  } catch {
    return null
  }
}

/**
 * Clear the breadcrumb. Called on explicit "Exit tenant view" (so a
 * deliberate exit never shows the accidental-drop notice) and when the
 * operator dismisses the notice.
 */
export function clearTenantViewMarker(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Storage unavailable — no-op.
  }
}
